import { useMemo, useState } from 'react';

import {
	CommunicationChannelKey,
	CommunicationPreferenceChange,
	CommunicationPreferenceRow,
	CommunicationPreferences,
	UpdateCommunicationPreferencesPayload,
} from '../Types/CommunicationPreferences';

const cellKey = (category: string, channel: CommunicationChannelKey) => `${category}:${channel}`;

/**
 * Holds the edits made on a preferences screen until they are saved.
 *
 * <p>Both screens stage rather than saving each tick. A preference screen that writes on every click
 * has no undo: a mis-click switches off a message the customer wanted and nothing tells them it
 * happened. Staging also means one request per save rather than one per checkbox, which matters for
 * the staff screen, where every save writes a note and an audit row.</p>
 *
 * <p>Only genuine differences are reported. Ticking a box and unticking it again leaves nothing to
 * save, so the buttons disappear again rather than sending a no-op.</p>
 */
export function usePreferenceEdits(data: CommunicationPreferences | undefined) {
	const [cells, setCells] = useState<Record<string, boolean>>({});
	const [smsMaster, setSmsMaster] = useState<boolean | undefined>();
	const [marketing, setMarketing] = useState<boolean | undefined>();

	const valueOf = (row: CommunicationPreferenceRow, channel: CommunicationChannelKey) =>
		cells[cellKey(row.key, channel)] ?? row[channel].enabled;

	const setCell = (row: CommunicationPreferenceRow, channel: CommunicationChannelKey, enabled: boolean) =>
		setCells(current => ({ ...current, [cellKey(row.key, channel)]: enabled }));

	const changes: CommunicationPreferenceChange[] = useMemo(() => {
		if (!data) {
			return [];
		}

		return data.rows.flatMap(row =>
			(['email', 'sms'] as CommunicationChannelKey[])
				.filter(channel => {
					const staged = cells[cellKey(row.key, channel)];
					return staged !== undefined && staged !== row[channel].enabled;
				})
				.map(channel => ({
					category: row.key,
					channel,
					enabled: cells[cellKey(row.key, channel)],
				})),
		);
	}, [data, cells]);

	// Marketing is tri-state: `null` means never answered, and answering it for the first time is a
	// change even though the screen showed it unticked either way.
	const smsMasterChanged = data != null && smsMaster !== undefined && smsMaster !== data.isSubscribedToSms;
	const marketingChanged =
		data != null && marketing !== undefined && marketing !== (data.isSubscribedToMarketing === true);

	const discard = () => {
		setCells({});
		setSmsMaster(undefined);
		setMarketing(undefined);
	};

	const payload = (reason?: string): UpdateCommunicationPreferencesPayload => ({
		changes,
		isSubscribedToSms: smsMasterChanged ? smsMaster : undefined,
		isSubscribedToMarketing: marketingChanged ? marketing : undefined,
		reason,
	});

	return {
		valueOf,
		setCell,
		smsMasterValue: smsMaster ?? data?.isSubscribedToSms ?? false,
		setSmsMaster,
		marketingValue: marketing ?? data?.isSubscribedToMarketing === true,
		setMarketing,
		changes,
		smsMasterChanged,
		marketingChanged,
		hasChanges: changes.length > 0 || smsMasterChanged || marketingChanged,
		discard,
		payload,
	};
}
